<template>
  <div class="modern-auth-wrapper">
    <!-- Animated blob canvas background -->
    <canvas id="blobCanvas" ref="blobCanvas"></canvas>

    <div class="auth-container">
      <!-- Left visual panel with fluid logo -->
      <div class="side-visual">
        <div class="fluid-logo-container">
          <img 
            :src="staffSyncLogo" 
            @error="handleLogoError"
            alt="STAFF SYNC Logo" 
            class="fluid-logo"
          >
          <div class="fluid-orb-overlay"></div>
        </div>

        <div class="brand-title">STAFF SYNC</div>
        <div class="brand-sub">Human Resource Portal</div>

        <div class="status-pill">
          <div class="status-dot"></div>
          Secure connection active
        </div>
      </div>

      <!-- Right form panel -->
      <div class="form-side">
        <div class="welcome-text">Welcome back</div>
        <div class="welcome-sub">Sign in to continue your session</div>

        <!-- Username Field -->
        <div class="field">
          <label class="field-label" for="uname">Username / ID Number</label>
          <div class="input-wrap">
            <span class="input-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </span>
            <input
              id="uname"
              v-model="username"
              type="text"
              class="fluid-input"
              placeholder="Enter username or ID"
              @keyup.enter="login"
              autocomplete="username"
            />
          </div>
        </div>

        <!-- Password Field -->
        <div class="field">
          <label class="field-label" for="pw">Password</label>
          <div class="input-wrap">
            <span class="input-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </span>
            <input
              id="pw"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              class="fluid-input"
              placeholder="Enter your password"
              @keyup.enter="login"
              autocomplete="current-password"
            />
            <button class="toggle-pw" @click="showPassword = !showPassword" type="button">
              <svg v-if="!showPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Options row -->
        <div class="row-opts">
          <label class="check-wrap">
            <div class="c-box" :class="{ checked: rememberMe }"></div>
            <input type="checkbox" v-model="rememberMe" class="hidden-checkbox" />
            <span class="check-label">Remember me</span>
          </label>
          <a href="javascript:void(0);" @click="goToForgotPassword" class="forgot">Forgot password?</a>
        </div>

        <!-- Alert Messages -->
        <div class="alert-box" :class="{ show: alert.success || alert.info || alert.failed, ok: alert.success, info: alert.info, err: alert.failed }">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{{ message }}</span>
        </div>

        <!-- Login Button -->
        <button class="btn-sign" :disabled="isLoading" @click="login">
          <template v-if="!isLoading">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            <span>Sign In</span>
          </template>
          <template v-else>
            <div class="spinner"></div>
            <span>Signing In...</span>
          </template>
        </button>

        <div class="foot">© {{ currentYear }} <span>STAFF SYNC</span>. All rights reserved.</div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, execute } from "@/api";
import router from "@/router";

export default {
  name: "LoginPage",
  data() {
    return {
      // Try multiple paths for the logo to ensure it loads in production
      staffSyncLogo: require('@/assets/staffsynclogo.png'),
      logoFallback: '',
      username: '',
      password: '',
      rememberMe: false,
      showPassword: false,
      isLoading: false,
      message: '',
      alert: {
        success: false,
        info: false,
        failed: false
      },
      deviceDetails: '',
      currentYear: new Date().getFullYear()
    }
  },
  mounted() {
    this.getDeviceDetails();
    this.loadRememberedCredentials();
    this.initBlobCanvas();
    this.initFluidLogoAnimation();
    
    // Try alternative logo paths if needed
    this.ensureLogoLoads();
  },
  methods: {
    goToForgotPassword() {
      this.$router.push('/forgot-password');
    },
    
    ensureLogoLoads() {
      // If the require path fails, try direct path
      if (!this.staffSyncLogo) {
        this.staffSyncLogo = '/staffsynclogo.png';
      }
    },
    
    handleLogoError() {
      console.warn('Logo failed to load, trying fallback paths');
      // Try different paths if the logo fails to load
      const fallbackPaths = [
        '/staffsynclogo.png',
        '/public/staffsynclogo.png',
        '/img/staffsynclogo.png',
        '/images/staffsynclogo.png',
        '/assets/staffsynclogo.png',
        require('@/assets/staffsynclogo.png'),
        '/staffsync/public/staffsynclogo.png'
      ];
      
      // Try the next fallback path
      if (this.logoFallbackIndex === undefined) {
        this.logoFallbackIndex = 0;
      }
      
      if (this.logoFallbackIndex < fallbackPaths.length) {
        this.staffSyncLogo = fallbackPaths[this.logoFallbackIndex];
        this.logoFallbackIndex++;
      } else {
        // If all fails, use a text-based logo or hide
        console.error('Could not load logo from any path');
        // Optionally show a text fallback
        const container = document.querySelector('.fluid-logo-container');
        if (container && !container.querySelector('.logo-fallback')) {
          const fallbackDiv = document.createElement('div');
          fallbackDiv.className = 'logo-fallback';
          fallbackDiv.innerHTML = '<svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
          fallbackDiv.style.cssText = 'width:100px;height:100px;background:rgba(255,255,255,0.2);border-radius:50%;display:flex;align-items:center;justify-content:center;';
          container.appendChild(fallbackDiv);
          const img = container.querySelector('img');
          if (img) img.style.display = 'none';
        }
      }
    },

    getDeviceDetails() {
      let os = 'Unknown';
      if (navigator.userAgent.indexOf('Win') !== -1) os = 'Windows';
      else if (navigator.userAgent.indexOf('Mac') !== -1) os = 'MacOS';
      else if (navigator.userAgent.indexOf('Linux') !== -1) os = 'Linux';
      else if (navigator.userAgent.indexOf('Android') !== -1) os = 'Android';
      else if (navigator.userAgent.indexOf('iOS') !== -1) os = 'iOS';

      let browser = 'Unknown';
      if (navigator.userAgent.indexOf('Chrome') !== -1) browser = 'Chrome';
      else if (navigator.userAgent.indexOf('Firefox') !== -1) browser = 'Firefox';
      else if (navigator.userAgent.indexOf('Safari') !== -1) browser = 'Safari';
      else if (navigator.userAgent.indexOf('Edge') !== -1) browser = 'Edge';
      else if (navigator.userAgent.indexOf('MSIE') !== -1 || !!document.documentMode) browser = 'Internet Explorer';

      const screenResolution = `${window.screen.width} x ${window.screen.height}`;
      const cpuCores = navigator.hardwareConcurrency || 'Unknown';
      const memory = navigator.deviceMemory || 'Unknown';

      this.deviceDetails = `os: ${os} | browser: ${browser} | screenResolution: ${screenResolution} | cpuCores: ${cpuCores} | memory: ${memory}`;
    },

    loadRememberedCredentials() {
      try {
        const rememberedUsername = localStorage.getItem('rememberedUsername');
        const rememberedPassword = localStorage.getItem('rememberedPassword');

        if (rememberedUsername) {
          this.username = rememberedUsername;
          this.rememberMe = true;

          if (rememberedPassword) {
            try {
              this.password = atob(rememberedPassword);
            } catch (e) {
              console.error('Error decoding saved password');
              localStorage.removeItem('rememberedPassword');
            }
          }
        }
      } catch (error) {
        console.error('Error loading remembered credentials:', error);
      }
    },

    saveRememberedCredentials() {
      try {
        if (this.rememberMe) {
          localStorage.setItem('rememberedUsername', this.username);
          localStorage.setItem('rememberedPassword', btoa(this.password));
        } else {
          localStorage.removeItem('rememberedUsername');
          localStorage.removeItem('rememberedPassword');
        }
      } catch (error) {
        console.error('Error saving remembered credentials:', error);
      }
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    async login() {
      if (!this.username || !this.password) {
        this.clearAlerts();
        this.message = "Please enter both username and password";
        this.alert.failed = true;
        return;
      }

      this.clearAlerts();
      this.message = "Authorizing your credentials...";
      this.alert.info = true;
      this.isLoading = true;

      const data = new FormData();
      data.append("function", "loginPortal");
      data.append("email", this.username);
      data.append("password", btoa(this.password));
      data.append("version", "1");
      data.append("deviceDetails", this.deviceDetails);

      try {
        const res = await execute(data, authUrl);

        this.clearAlerts();
        this.isLoading = false;

        if (res.data.success) {
          this.saveRememberedCredentials();

          const token = res.data.data.tokenSession.token;
          sessionStorage.setItem("tokenSession", token);

          if (Array.isArray(res.data.data?.roles)) {
            sessionStorage.setItem("selectedRoles", JSON.stringify(res.data.data.roles));
            window.dispatchEvent(new Event("staffsync-roles-updated"));
          }

          if (!res.data?.data?.user) {
            if (res.data.data.otpLength) {
              sessionStorage.setItem("otpLength", res.data.data.otpLength);
            }
            router.push('otp-verification');
          } else {
            const userData = res.data.data.user;

            sessionStorage.setItem("isLoggedIn", "true");
            sessionStorage.setItem("userInfo", JSON.stringify({
              names: userData.names,
              idNo: userData.idNo,
              phoneNumber: userData.phoneNumber,
              category: userData.category,
              requireOTP: userData.requireOTP,
              passwordChanged: userData.passwordChanged,
              passwordExpiry: userData.passwordExpiry
            }));

            this.message = res.data.message || "Login successful!";
            this.alert.success = true;

            if (userData.passwordChanged === 'false' || userData.passwordChanged === false || userData.passwordChanged === '0') {
              setTimeout(() => {
                router.push('change-password');
              }, 500);
              return;
            }

            if (userData.requireOTP === 'true') {
              if (res.data.data.otpLength) {
                sessionStorage.setItem("otpLength", res.data.data.otpLength);
              }
              router.push('otp');
            } else {
              setTimeout(() => {
                const category = (userData.category || '').toUpperCase();
                if (category === 'SUPERADMIN') {
                  router.push('super-admin');
                } else if (category === 'EMPLOYEE' || category === 'EMPLOYEES') {
                  router.push('employee-dashboard');
                } else {
                  router.push('dashboard');
                }
              }, 500);
            }
          }
        } else {
          sessionStorage.setItem("isLoggedIn", "false");
          this.alert.failed = true;
          this.message = res.data.message || "Login failed. Please check your credentials.";
        }
      } catch (error) {
        this.clearAlerts();
        this.isLoading = false;
        this.alert.failed = true;
        this.message = error.message || "An error occurred during login. Please try again.";
        console.error("Login error:", error);
      }
    },

    initBlobCanvas() {
      const canvas = this.$refs.blobCanvas;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      let W, H;
      let mouse = { x: 0.5, y: 0.5 };
      
      const blobs = [
        { ox: 0.15, oy: 0.20, r: 220, c: 'rgba(100,80,255,0.12)', phase: 0, amp: 0.07 },
        { ox: 0.85, oy: 0.80, r: 250, c: 'rgba(140,80,255,0.09)', phase: 1.5, amp: 0.08 },
        { ox: 0.50, oy: 0.50, r: 160, c: 'rgba(80,130,255,0.08)', phase: 3.0, amp: 0.04 },
        { ox: 0.80, oy: 0.12, r: 140, c: 'rgba(200,120,255,0.09)', phase: 0.8, amp: 0.06 },
        { ox: 0.10, oy: 0.88, r: 150, c: 'rgba(80,200,255,0.07)', phase: 2.2, amp: 0.06 },
      ];
      
      function resize() {
        W = canvas.width = window.innerWidth;
        H = canvas.height = window.innerHeight;
      }
      
      let t = 0;
      function draw() {
        ctx.clearRect(0, 0, W, H);
        t += 0.014;
        blobs.forEach(b => {
          const bx = (b.ox + Math.sin(t + b.phase) * b.amp + (mouse.x - 0.5) * 0.05) * W;
          const by = (b.oy + Math.cos(t * 0.75 + b.phase) * b.amp * 0.8 + (mouse.y - 0.5) * 0.05) * H;
          const grd = ctx.createRadialGradient(bx, by, 0, bx, by, b.r);
          grd.addColorStop(0, b.c);
          grd.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.beginPath();
          ctx.arc(bx, by, b.r, 0, Math.PI * 2);
          ctx.fillStyle = grd;
          ctx.fill();
        });
        requestAnimationFrame(draw);
      }
      
      resize();
      draw();
      window.addEventListener('resize', resize);
      document.addEventListener('mousemove', (e) => {
        mouse.x = e.clientX / window.innerWidth;
        mouse.y = e.clientY / window.innerHeight;
      });
    },

    initFluidLogoAnimation() {
      const logo = document.querySelector('.fluid-logo');
      const overlay = document.querySelector('.fluid-orb-overlay');
      if (!logo) return;
      
      let angle = 0;
      let scale = 1;
      let direction = 1;
      
      setInterval(() => {
        angle += 0.02;
        const yOffset = Math.sin(angle) * 8;
        const xOffset = Math.cos(angle * 0.7) * 5;
        
        if (logo) {
          logo.style.transform = `translate(${xOffset}px, ${yOffset}px) scale(${scale})`;
        }
        
        scale += direction * 0.003;
        if (scale >= 1.03) direction = -1;
        if (scale <= 0.97) direction = 1;
        
        if (overlay) {
          overlay.style.transform = `translate(-50%, -50%) rotate(${angle * 30}deg) scale(1.1)`;
        }
      }, 50);
    }
  }
}
</script>

<style scoped>
*, *::before, *::after { 
  box-sizing: border-box; 
  margin: 0; 
  padding: 0; 
}

.modern-auth-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #eeeef6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

#blobCanvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  display: block;
}

.auth-container {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 90%;
  max-width: 860px;
  min-height: 520px;
  max-height: 90vh;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.95);
  border-radius: 32px;
  box-shadow: 0 8px 48px rgba(60, 60, 120, 0.14), 0 1px 0 rgba(255,255,255,0.9) inset;
  overflow: hidden;
  animation: cardIn 0.55s cubic-bezier(0.4, 0, 0.2, 1) both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Left visual panel with fluid logo */
.side-visual {
  position: relative;
  background: #152c56;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  overflow: hidden;
}

.side-visual::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 30% 20%, rgba(255,255,255,0.18) 0%, transparent 55%),
    radial-gradient(circle at 80% 80%, rgba(120,80,255,0.3) 0%, transparent 50%);
}

.fluid-logo-container {
  position: relative;
  z-index: 1;
  width: 180px;
  height: 180px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fluid-logo {
  position: relative;
  z-index: 2;
  width: 140px;
  height: auto;
  max-height: 140px;
  object-fit: contain;
  filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.2));
  transition: transform 0.1s ease-out;
}

.fluid-orb-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 170px;
  height: 170px;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%);
  border-radius: 50%;
  pointer-events: none;
  transition: transform 0.1s linear;
}

.brand-title {
  font-family: 'Syne', 'DM Sans', sans-serif;
  font-size: 23px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.5px;
  text-align: center;
  position: relative;
  z-index: 1;
  margin-bottom: 6px;
}

.brand-sub {
  font-size: 11px;
  color: rgba(255,255,255,0.65);
  text-align: center;
  position: relative;
  z-index: 1;
  letter-spacing: 0.7px;
  text-transform: uppercase;
}

.status-pill {
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255,255,255,0.14);
  border: 1px solid rgba(255,255,255,0.26);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 11px;
  color: rgba(255,255,255,0.82);
  display: flex;
  align-items: center;
  gap: 7px;
  backdrop-filter: blur(8px);
  z-index: 1;
  white-space: nowrap;
}

.status-dot {
  width: 7px;
  height: 7px;
  background: #4ade80;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.55; transform: scale(0.75); }
}

/* Right form panel */
.form-side {
  padding: 44px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
}

.welcome-text {
  font-family: 'Syne', 'DM Sans', sans-serif;
  font-size: 27px;
  font-weight: 700;
  color: #1a1a2e;
  letter-spacing: -0.5px;
  margin-bottom: 4px;
}

.welcome-sub {
  font-size: 13px;
  color: #8892a4;
  margin-bottom: 30px;
}

.field { margin-bottom: 18px; }

.field-label {
  font-size: 10.5px;
  font-weight: 600;
  color: #253759;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  margin-bottom: 7px;
  display: block;
}

.input-wrap { position: relative; }

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #aab0bf;
  pointer-events: none;
  display: flex;
  align-items: center;
}

.fluid-input {
  width: 100%;
  padding: 12px 14px 12px 42px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: #1a1a2e;
  background: rgba(248, 249, 255, 0.85);
  border: 1.5px solid #e2e5f0;
  border-radius: 12px;
  outline: none;
  transition: border-color 0.22s, box-shadow 0.22s, background 0.22s;
}

.fluid-input:focus {
  border-color: #253759;
  background: #fff;
  box-shadow: 0 0 0 3.5px rgba(74, 58, 255, 0.11);
}

.fluid-input::placeholder { color: #b8bfcc; }

.toggle-pw {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #b0b7c3;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}
.toggle-pw:hover { color: #253759; }

.row-opts {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.check-wrap {
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  user-select: none;
}

.hidden-checkbox {
  display: none;
}

.c-box {
  width: 17px;
  height: 17px;
  border: 1.5px solid #d1d5db;
  border-radius: 5px;
  background: #fff;
  position: relative;
  flex-shrink: 0;
  transition: background 0.18s, border-color 0.18s;
}
.c-box.checked {
  background: #253759;
  border-color: #253759;
}
.c-box.checked::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1.5px;
  width: 6px;
  height: 10px;
  border: 2px solid #fff;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
}

.check-label { font-size: 12.5px; color: #6b7280; }

.forgot {
  font-size: 12.5px;
  color: #253759;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s;
  cursor: pointer;
}
.forgot:hover { opacity: 0.75; text-decoration: underline; }

/* Alert box */
.alert-box {
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 12.5px;
  display: none;
  align-items: center;
  gap: 9px;
  margin-bottom: 16px;
}
.alert-box.show { display: flex; }
.alert-box.err  { background: rgba(239,68,68,0.08); color: #b91c1c; border: 1px solid rgba(239,68,68,0.22); }
.alert-box.info { background: rgba(74,58,255,0.07); color: #3730a3; border: 1px solid rgba(74,58,255,0.2); }
.alert-box.ok   { background: rgba(74,222,128,0.09); color: #166534; border: 1px solid rgba(74,222,128,0.25); }

.btn-sign {
  width: 100%;
  padding: 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background: #152c56;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  letter-spacing: 0.2px;
  margin-bottom: 24px;
}

.public-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
  align-items: center;
}

.careers-link {
  font-size: 13px;
  color: #64748b;
  text-decoration: none;
  transition: all 0.2s;
  background: #f8fafc;
  padding: 10px 16px;
  border-radius: 8px;
  width: 100%;
  text-align: center;
  border: 1px solid #e2e8f0;
}

.careers-link span {
  font-weight: 600;
  color: #4f46e5;
}

.careers-link:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.btn-sign::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%);
  border-radius: inherit;
}

.btn-sign:hover:not(:disabled)  { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(74,58,255,0.46); }
.btn-sign:active:not(:disabled) { transform: translateY(0);    box-shadow: 0 2px 12px rgba(74,58,255,0.28); }
.btn-sign:disabled               { opacity: 0.68; cursor: not-allowed; }

.spinner {
  width: 17px; height: 17px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.foot {
  text-align: center;
  padding-top: 22px;
  font-size: 11px;
  color: #b0b7c3;
  letter-spacing: 0.3px;
}
.foot span { color: #4a3aff; font-weight: 600; }

/* Responsive */
@media (max-width: 680px) {
  .auth-container { 
    grid-template-columns: 1fr; 
    width: 95%;
    max-width: 480px;
  }
  .side-visual { display: none; }
  .form-side { padding: 36px 26px; }
}
</style>